import { type SalesLeadActivity, SalesLeadActivityType } from '@/models/salesLead'

/** The kinds of entry the timeline can be narrowed to, in the order the filter lists them. */
export enum ActivityGroup {
  NOTES = 'NOTES',
  SYSTEM_NOTES = 'SYSTEM_NOTES',
  EMAILS = 'EMAILS',
  CALLS = 'CALLS',
  TEXTS = 'TEXTS',
  ONBOARDING = 'ONBOARDING',
  QUOTES = 'QUOTES',
  POLICY_AND_CLAIMS = 'POLICY_AND_CLAIMS',
  ACCOUNT = 'ACCOUNT',
  CAI = 'CAI',
  STATUS = 'STATUS',
}

export const activityGroupLabels: Record<ActivityGroup, string> = {
  [ActivityGroup.NOTES]: 'Notes',
  [ActivityGroup.SYSTEM_NOTES]: 'System notes',
  [ActivityGroup.EMAILS]: 'Emails',
  [ActivityGroup.CALLS]: 'Calls',
  [ActivityGroup.TEXTS]: 'Texts',
  [ActivityGroup.ONBOARDING]: 'Onboarding',
  [ActivityGroup.QUOTES]: 'Quotes and carriers',
  [ActivityGroup.POLICY_AND_CLAIMS]: 'Policy and claims',
  [ActivityGroup.ACCOUNT]: 'Account',
  [ActivityGroup.CAI]: 'Cai',
  [ActivityGroup.STATUS]: 'Status and edits',
}

const activityGroupByType: Record<SalesLeadActivityType, ActivityGroup> = {
  [SalesLeadActivityType.NOTE]: ActivityGroup.NOTES,
  [SalesLeadActivityType.BROKER_NOTE]: ActivityGroup.NOTES,

  [SalesLeadActivityType.EMAIL]: ActivityGroup.EMAILS,
  [SalesLeadActivityType.EMAIL_INBOUND]: ActivityGroup.EMAILS,
  [SalesLeadActivityType.EMAIL_OUTBOUND]: ActivityGroup.EMAILS,
  [SalesLeadActivityType.EMAIL_OUTBOUND_DRAFT]: ActivityGroup.EMAILS,

  [SalesLeadActivityType.CALL]: ActivityGroup.CALLS,
  [SalesLeadActivityType.CALL_INBOUND]: ActivityGroup.CALLS,
  [SalesLeadActivityType.CALL_INBOUND_MISSED]: ActivityGroup.CALLS,
  [SalesLeadActivityType.CALL_OUTBOUND]: ActivityGroup.CALLS,
  [SalesLeadActivityType.CALL_OUTBOUND_MISSED]: ActivityGroup.CALLS,

  [SalesLeadActivityType.SMS]: ActivityGroup.TEXTS,
  [SalesLeadActivityType.SMS_INBOUND]: ActivityGroup.TEXTS,
  [SalesLeadActivityType.SMS_OUTBOUND]: ActivityGroup.TEXTS,
  [SalesLeadActivityType.SMS_OUTBOUND_DRAFT]: ActivityGroup.TEXTS,

  // The way to a ready application.
  [SalesLeadActivityType.WEB_DOT_ENTRY]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.SIGNED_UP]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.EMAIL_VERIFIED]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.FUNNEL_NEEDS_REVIEW]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.APPLICATION_STARTED]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.APPLICATION_COMPLETED]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.APPLICATION_SUBMITTED]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.DOCUMENT_SIGNED]: ActivityGroup.ONBOARDING,
  [SalesLeadActivityType.ELD_CONNECTED]: ActivityGroup.ONBOARDING,

  // What carriers need, ask and answer.
  [SalesLeadActivityType.CARRIER_ELIGIBLE]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.CARRIER_INELIGIBLE]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.LOSS_RUN_UPDATE]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.MVR_COMPLETED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.LARGE_FLEET_PACKET]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.SENT_TO_CARRIERS]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.REQUOTE_REQUESTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.REQUOTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.INDICATION_RECEIVED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.INDICATION_FAILED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.NO_CARRIER_QUOTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.QUOTES_SENT]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.QUOTES_COMPARED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.QUOTE_SHORTLISTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.INDICATIONS_SELECTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.QUOTE_READY]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.QUOTE_SELECTED]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.CARRIER_UPDATE]: ActivityGroup.QUOTES,
  [SalesLeadActivityType.CARRIER_ISSUE]: ActivityGroup.QUOTES,

  [SalesLeadActivityType.POLICY_BOUND]: ActivityGroup.POLICY_AND_CLAIMS,
  [SalesLeadActivityType.POLICY_CANCELED]: ActivityGroup.POLICY_AND_CLAIMS,
  [SalesLeadActivityType.CLAIM_REPORTED]: ActivityGroup.POLICY_AND_CLAIMS,
  [SalesLeadActivityType.CLAIM_SUBMITTED]: ActivityGroup.POLICY_AND_CLAIMS,
  [SalesLeadActivityType.ENDORSEMENT_SUBMITTED]: ActivityGroup.POLICY_AND_CLAIMS,
  [SalesLeadActivityType.ENDORSEMENT_UPDATED]: ActivityGroup.POLICY_AND_CLAIMS,

  [SalesLeadActivityType.SIGNED_IN]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.APP_OPENED]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.USER_INVITED]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.USER_JOINED]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.ACCOUNT_LINKED]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.DUPLICATE_BLOCKED]: ActivityGroup.ACCOUNT,
  [SalesLeadActivityType.ORGS_MERGED]: ActivityGroup.ACCOUNT,

  [SalesLeadActivityType.ASKED_CAI]: ActivityGroup.CAI,
  [SalesLeadActivityType.AI_SUMMARY]: ActivityGroup.CAI,

  [SalesLeadActivityType.STATUS_CHANGE]: ActivityGroup.STATUS,
  [SalesLeadActivityType.FIELD_UPDATE]: ActivityGroup.STATUS,
}

export function activityGroup(activity: SalesLeadActivity): ActivityGroup {
  const group = activityGroupByType[activity.activityType]
  // The system's own notes (a web visit, an assignment, a reminder) would bury people's.
  const isAutomated = activity.createdByKind !== undefined && activity.createdByKind !== 'user'
  return group === ActivityGroup.NOTES && isAutomated ? ActivityGroup.SYSTEM_NOTES : group
}

/** The activities in any of the groups, or all of them when none is picked. */
export function inGroups(activities: SalesLeadActivity[], groups: ActivityGroup[]): SalesLeadActivity[] {
  if (groups.length === 0) return activities
  return activities.filter((activity) => groups.includes(activityGroup(activity)))
}
